import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { datadogRum } from '@datadog/browser-rum'
import { IS_PRODUCTION } from 'src/constants'

const sanitizeHeaders = (headers: any) => {
  if (!headers) return undefined

  const sensitiveHeaders = [
    'authorization',
    'x-api-key',
    'api-key',
    'cookie',
    'set-cookie',
    'x-auth-token',
  ]

  const sanitizedHeaders = { ...headers }
  sensitiveHeaders.forEach(header => {
    const headerKey = Object.keys(sanitizedHeaders).find(
      key => key.toLowerCase() === header.toLowerCase()
    )

    if (headerKey && sanitizedHeaders[headerKey]) {
      sanitizedHeaders[headerKey] = '[REDACTED]'
    }
  })

  return sanitizedHeaders
}

const sanitizeRequestPayload = (request?: InternalAxiosRequestConfig<any>, response?: AxiosResponse<any, any>) => {
  const urlLastPath = '/' + request?.url?.split('/').pop()
  // Contact record validation failed
  if (
    request?.method?.toUpperCase() === 'POST' && 
    urlLastPath === '/contacts' &&
    response?.data?.five9ExceptionDetail?.errorCode === 12032
  ) {
    const allowedFieldTypes = IS_PRODUCTION ? [
      '1100', '1101', '1102', '1103', '1104', '1105', '1106', '1107', '1108', '1018', '1019', '1020'
    ] : [
      '300000000001005', '1008', '1009', '1010'
    ]

    const parsedRequestPayload = JSON.parse(request?.data)
    return {
      fields: allowedFieldTypes.reduce((sanitized, fieldType) => ({
        ...sanitized,
        [fieldType]: parsedRequestPayload?.fields?.[fieldType]
      }), {})
    }
  }
}

export const addAxiosInterceptor = () => {
  axios.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const errorContext = {
        source: 'axios',
        errorType: 'api_error',
        isAxiosError: axios.isAxiosError(error),
        requestInfo: {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          baseURL: error.config?.baseURL,
          timeout: error.config?.timeout,
          headers: sanitizeHeaders(error.config?.headers),
          sanitizedPayload: sanitizeRequestPayload(error.config, error.response)
        },
        responseInfo: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          headers: sanitizeHeaders(error.response.headers),
          data: error.response.data,
        } : undefined,
        networkError: !error.response && error.request ? true : undefined,
        errorMessage: error.message,
      }

      if (error.response?.data?.five9ExceptionDetail) {
        // Create a custom error with a user-friendly message for DataDog
        const urlLastPath = '/' + error.config?.url?.split('/').pop()
        const customError = new Error(`${error.config?.method?.toUpperCase()} ${urlLastPath} - ${error.response.data.five9ExceptionDetail.message}`)
        // Preserve the original error's stack trace and properties
        customError.stack = error.stack
        customError.name = 'Five9Error'
        datadogRum.addError(customError, errorContext)

        try {
          console.log(
            'Five9 exception',
            JSON.stringify(error.response?.data?.five9ExceptionDetail, null, 2)
          )
        } catch (stringifyError) {
          console.log('Five9 exception', error.response?.data?.five9ExceptionDetail)
        }
      } else {
        datadogRum.addError(error, errorContext)
      }

      return Promise.reject(error)
    }
  )
}
